<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>

          <!-- ══════════════════════════════════════════════════════════
               1. ACCOUNT & LOGIN
          ══════════════════════════════════════════════════════════ -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-user-circle"></i></span>
            <span>{{ $t('teachers.sectionAccount') }}</span>
          </div>
          <div class="row g-3 mb-4">

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.username') }} <span class="text-danger">*</span></label>
              <input type="text" class="form-control borderless-input" :class="{ 'is-invalid': errors.username }"
                     v-model.trim="formData.username" required @blur="validateField('username')" />
              <div class="invalid-feedback">{{ errors.username }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.email') }} <span class="text-danger">*</span></label>
              <input type="email" class="form-control borderless-input" :class="{ 'is-invalid': errors.email }"
                     v-model.trim="formData.email" required @blur="validateEmail()" />
              <div class="invalid-feedback">{{ errors.email }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('teachers.password') }}
                <span v-if="!isEditMode" class="text-danger">*</span>
              </label>
              <input type="password" class="form-control borderless-input" :class="{ 'is-invalid': errors.password }"
                     v-model="formData.password" :required="!isEditMode" @blur="validatePassword()" />
              <div class="invalid-feedback">{{ errors.password }}</div>
              <small v-if="isEditMode" class="text-muted">{{ $t('teachers.passwordHint') }}</small>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('teachers.confirmPassword') }}
                <span v-if="!isEditMode" class="text-danger">*</span>
              </label>
              <input type="password" class="form-control borderless-input" :class="{ 'is-invalid': errors.password_confirmation }"
                     v-model="formData.password_confirmation" :required="!isEditMode" @blur="validateConfirmPassword()" />
              <div class="invalid-feedback">{{ errors.password_confirmation }}</div>
            </div>

          </div>

          <!-- ══════════════════════════════════════════════════════════
               2. PERSONAL INFO
          ══════════════════════════════════════════════════════════ -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-id-card"></i></span>
            <span>{{ $t('teachers.sectionPersonal') }}</span>
          </div>
          <div class="row g-3 mb-4">

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.gender') }} <span class="text-danger">*</span></label>
              <select class="form-select borderless-input" v-model="formData.gender" required>
                <option value="M">{{ $t('teachers.male') }}</option>
                <option value="F">{{ $t('teachers.female') }}</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.nationality') }} <span class="text-danger">*</span></label>
              <select class="form-select borderless-input" :class="{ 'is-invalid': errors.nationality_id }"
                      v-model="formData.nationality_id" required @change="handleNationalityChange">
                <option value="">{{ $t('teachers.selectNationality') }}</option>
                <option v-if="isNationalitiesLoading" disabled>{{ $t('teachers.loadingNationalities') }}</option>
                <option v-else v-for="nat in nationalities" :key="nat.id" :value="nat.id">
                  {{ localizedNatName(nat) }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.nationality_id }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('teachers.nationalId') }}
                <span v-if="isLibyan" class="text-danger">*</span>
                <span v-else class="text-muted small">({{ $t('teachers.libyansOnly') }})</span>
              </label>
              <input type="text" class="form-control borderless-input" :class="{ 'is-invalid': errors.national_num }"
                     v-model.trim="formData.national_num" :required="isLibyan" :disabled="!isLibyan"
                     @blur="validateNationalNumber()"
                     :placeholder="isLibyan ? $t('teachers.nationalIdRequired') : $t('teachers.libyansOnly')" />
              <div class="invalid-feedback">{{ errors.national_num }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('teachers.passport') }}
                <span v-if="!isLibyan" class="text-danger">*</span>
                <span v-else class="text-muted small">({{ $t('teachers.nonLibyansOnly') }})</span>
              </label>
              <input type="text" class="form-control borderless-input" :class="{ 'is-invalid': errors.passport_num }"
                     v-model.trim="formData.passport_num" :required="!isLibyan" :disabled="isLibyan"
                     @input="clearError('passport_num')" @blur="validatePassport()"
                     :placeholder="!isLibyan ? $t('teachers.passportRequired') : $t('teachers.nonLibyansOnly')" />
              <div class="invalid-feedback">{{ errors.passport_num }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.phone') }} <span class="text-danger">*</span></label>
              <input type="tel" class="form-control borderless-input" :class="{ 'is-invalid': errors.phone_num }"
                     v-model.trim="formData.phone_num" required @blur="validatePhone()" />
              <div class="invalid-feedback">{{ errors.phone_num }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.address') }} <span class="text-danger">*</span></label>
              <input type="text" class="form-control borderless-input" :class="{ 'is-invalid': errors.address }"
                     v-model.trim="formData.address" required @blur="validateAddress()" />
              <div class="invalid-feedback">{{ errors.address }}</div>
            </div>

          </div>

          <!-- ══════════════════════════════════════════════════════════
               3. PROFESSIONAL INFO
          ══════════════════════════════════════════════════════════ -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-graduation-cap"></i></span>
            <span>{{ $t('teachers.sectionProfessional') }}</span>
          </div>
          <div class="row g-3 mb-4">

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.jobTitle') }}</label>
              <select class="form-select borderless-input" v-model="formData.job_title_id">
                <option value="">—</option>
                <option v-for="jt in jobTitles" :key="jt.id" :value="jt.id">{{ jt.name }}</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.qualification') }} <span class="text-danger">*</span></label>
              <select class="form-select borderless-input" :class="{ 'is-invalid': errors.qualification }"
                      v-model="formData.qualification" required @blur="validateField('qualification')">
                <option value="">—</option>
                <option v-for="qual in qualifications" :key="qual" :value="qual">{{ qual }}</option>
              </select>
              <div class="invalid-feedback">{{ errors.qualification }}</div>
            </div>

            <!-- Specialization: dropdown + custom text -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.specialization') }}</label>
              <select class="form-select borderless-input" v-model="specializationPreset"
                      @change="onSpecializationPresetChange">
                <option value="">—</option>
                <option v-for="s in specializations" :key="s" :value="s">{{ s }}</option>
                <option value="_other">{{ $t('teachers.other') }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="specializationPreset === '_other'">
              <label class="bmd-label-float">{{ $t('teachers.specializationCustom') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.specialization"
                     :placeholder="$t('teachers.specializationCustomPlaceholder')" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float d-block mb-2">{{ $t('teachers.salaryType') }}</label>
              <select class="form-select borderless-input" v-model="formData.salary_type">
                <option value="fixed">{{ $t('teachers.salaryTypeFixed') }}</option>
                <option value="per_lesson">{{ $t('teachers.salaryTypePerLesson') }}</option>
                <option value="fixed_plus_lessons">{{ $t('teachers.salaryTypeFixedPlusLessons') }}</option>
              </select>
              <small class="text-muted">{{ $t('teachers.salaryTypeHint') }}</small>
            </div>

            <div class="col-md-6" v-if="formData.salary_type !== 'per_lesson'">
              <label class="bmd-label-float">{{ $t('teachers.salary') }} <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" step="0.01" min="0" class="form-control borderless-input"
                       :class="{ 'is-invalid': errors.salary }"
                       v-model="formData.salary" required @blur="validateSalary()" />
                <span class="input-group-text">د.ل</span>
              </div>
              <div class="invalid-feedback d-block">{{ errors.salary }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.hiringDate') }} <span class="text-danger">*</span></label>
              <input type="date" class="form-control borderless-input" :class="{ 'is-invalid': errors.hiring_date }"
                     v-model="formData.hiring_date" required @blur="validateHiringDate()" :max="maxDate" />
              <div class="invalid-feedback">{{ errors.hiring_date }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.registerDate') }} <span class="text-danger">*</span></label>
              <input type="date" class="form-control borderless-input" :class="{ 'is-invalid': errors.register_date }"
                     v-model="formData.register_date" required @blur="validateRegisterDate()" :max="maxDate" />
              <div class="invalid-feedback">{{ errors.register_date }}</div>
            </div>

          </div>

          <!-- ══════════════════════════════════════════════════════════
               4. HR INFO
          ══════════════════════════════════════════════════════════ -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-folder-open"></i></span>
            <span>{{ $t('teachers.hrInfo') }}</span>
          </div>
          <div class="row g-3 mb-4">

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.fileNumber') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.file_number" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.clearanceDate') }}</label>
              <input type="date" class="form-control borderless-input"
                     v-model="formData.clearance_date" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float d-block mb-2">{{ $t('teachers.isActive') }}</label>
              <div class="status-toggle">
                <button type="button"
                        class="status-btn"
                        :class="formData.is_active ? 'status-btn--active' : 'status-btn--inactive'"
                        @click="formData.is_active = !Boolean(formData.is_active)">
                  <i :class="formData.is_active ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                  <span>{{ formData.is_active ? $t('teachers.active') : $t('teachers.inactive') }}</span>
                </button>
                <small class="text-muted d-block mt-1">
                  {{ formData.is_active ? $t('teachers.activeHint') : $t('teachers.inactiveHint') }}
                </small>
              </div>
            </div>

          </div>

          <!-- ══════════════════════════════════════════════════════════
               5. BANKING INFO
          ══════════════════════════════════════════════════════════ -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-university"></i></span>
            <span>{{ $t('teachers.bankingInfo') }}</span>
          </div>
          <div class="row g-3 mb-4">

            <!-- Bank: dropdown + custom -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.bankName') }}</label>
              <select class="form-select borderless-input" v-model="bankNamePreset"
                      @change="onBankPresetChange">
                <option value="">—</option>
                <option v-for="b in libyaBanks" :key="b" :value="b">{{ b }}</option>
                <option value="_other">{{ $t('teachers.other') }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="bankNamePreset === '_other'">
              <label class="bmd-label-float">{{ $t('teachers.bankNameCustom') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.bank_name"
                     :placeholder="$t('teachers.bankNameCustomPlaceholder')" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.bankBranch') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.bank_branch" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.accountNumber') }}</label>
              <input type="text" class="form-control borderless-input"
                     :class="{ 'is-invalid': errors.account_number }"
                     v-model.trim="formData.account_number"
                     maxlength="15" @input="validateAccountNumber"
                     placeholder="000000000000000" />
              <div class="invalid-feedback">{{ errors.account_number }}</div>
              <small class="text-muted">{{ $t('teachers.accountNumberHint') }}</small>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.iban') }}</label>
              <input type="text" class="form-control borderless-input"
                     :class="{ 'is-invalid': errors.iban }"
                     v-model.trim="formData.iban"
                     maxlength="23" @input="validateIban"
                     placeholder="LY00 0000 0000 0000 0000 000" />
              <div class="invalid-feedback">{{ errors.iban }}</div>
              <small class="text-muted">{{ $t('teachers.ibanHint') }}</small>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('teachers.salaryPaymentMethod') }}</label>
              <select class="form-select borderless-input" v-model="formData.salary_payment_method">
                <option value="">—</option>
                <option value="cash">{{ $t('teachers.salaryPaymentMethodCash') }}</option>
                <option value="certified_check">{{ $t('teachers.salaryPaymentMethodCertifiedCheck') }}</option>
                <option value="bank_transfer">{{ $t('teachers.salaryPaymentMethodBankTransfer') }}</option>
              </select>
            </div>

          </div>

          <!-- ══════════════════════════════════════════════════════════
               6. PROFILE PHOTO
          ══════════════════════════════════════════════════════════ -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-camera"></i></span>
            <span>{{ $t('teachers.profileImage') }}</span>
          </div>
          <div class="row g-3">
            <div class="col-12">
              <div class="photo-upload-area" @click="$refs.fileInput.click()">
                <input type="file" ref="fileInput" accept="image/*"
                       style="display:none" @change="handleImageUpload" />
                <div v-if="loadingImage" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status"></div>
                </div>
                <img v-else-if="previewImage" :src="previewImage"
                     class="img-fluid mx-auto d-block" style="max-height:160px" />
                <div v-else class="photo-upload-placeholder">
                  <i class="fas fa-cloud-upload-alt fs-1 text-muted"></i>
                  <p class="text-muted mt-2 mb-0">{{ $t('teachers.clickToUpload') }}</p>
                  <small class="text-muted">{{ $t('teachers.imageNote') }}</small>
                </div>
                <button v-if="previewImage" type="button" @click.stop="clearImage"
                        class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>

        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #344767;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}
.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(195deg, #2C91AE, #1E657A);
  box-shadow: 0 4px 6px -1px rgba(44, 145, 174, 0.35);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}
.photo-upload-area {
  position: relative;
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  min-height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.photo-upload-area:hover {
  border-color: #adb5bd;
}
.photo-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.status-toggle {
  display: flex;
}
.status-btn {
  border: none;
  border-radius: 20px;
  padding: 7px 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}
.status-btn i {
  font-size: 1rem;
}
.status-btn--active {
  background: linear-gradient(195deg, #4caf50, #3d8b40);
  color: #fff;
}
.status-btn--inactive {
  background: linear-gradient(195deg, #ef5350, #c62828);
  color: #fff;
}
</style>
<script setup>
import { ref, computed, onMounted, watch, defineEmits, defineProps, defineExpose, nextTick } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const props = defineProps({
  title: String,
  submitText: {
    type: String,
    },
  isSubmitting: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  initialData: {
    type: Object,
    default: () => ({
      username: '',
      email: '',
      password: '',
      password_confirmation: '',
      gender: 'M',
      nationality_id: '',
      national_num: '',
      passport_num: '',
      phone_num: '',
      address: '',
      qualification: '',
      specialization: '',
      hiring_date: '',
      salary: '',
      register_date: new Date().toISOString().split('T')[0],
      image: null,
      job_title_id: '',
      salary_type: 'fixed',
      // HR fields
      file_number: '',
      clearance_date: '',
      is_active: true,
      // Banking
      bank_name: '',
      bank_branch: '',
      account_number: '',
      iban: '',
      salary_payment_method: '',
    })
  }
});

const qualifications = ref([
  t('teachers.qualifications.bachelor'),
  t('teachers.qualifications.master'),
  t('teachers.qualifications.phd'),
  t('teachers.qualifications.diploma'),
  t('teachers.qualifications.highSchool')
]);

// ── Libyan banks list ─────────────────────────────────────────────────────
const libyaBanks = [
  'مصرف الجمهورية',
  'المصرف التجاري الوطني',
  'مصرف الوحدة',
  'مصرف الأمة',
  'مصرف الشمال الأفريقي',
  'مصرف الواحة',
  'مصرف الاستثمار والتنمية الريفية',
  'مصرف ليبيا الخارجي',
  'مصرف الخليج التجاري',
  'مصرف المتوسط',
  'مصرف السراي',
  'مصرف الإسكان والتعمير',
  'مصرف برقة',
  'مصرف التجارة والتنمية',
  'مصرف ليبيا المركزي',
];

// ── Specializations list ──────────────────────────────────────────────────
const specializations = [
  'اللغة العربية',
  'اللغة الإنجليزية',
  'اللغة الفرنسية',
  'الرياضيات',
  'الفيزياء',
  'الكيمياء',
  'الأحياء / علم الحياة',
  'العلوم العامة',
  'التاريخ',
  'الجغرافيا',
  'التربية الإسلامية',
  'التربية الوطنية',
  'الحاسوب وتقنية المعلومات',
  'التربية البدنية',
  'التربية الفنية',
  'رياض الأطفال',
  'التربية الخاصة',
  'الإدارة والاقتصاد',
];

// ── Preset refs for hybrid dropdown+text ──────────────────────────────────
const bankNamePreset       = ref('');
const specializationPreset = ref('');

const onBankPresetChange = (e) => {
  const val = e.target.value;
  if (val !== '_other') formData.value.bank_name = val;
  else formData.value.bank_name = '';
};

const onSpecializationPresetChange = (e) => {
  const val = e.target.value;
  if (val !== '_other') formData.value.specialization = val;
  else formData.value.specialization = '';
};
const localizedNatName = (nat) => {
  if (!nat) return '';
  const l = (locale.value || 'en').split('-')[0]; // ar-YE → ar
  return nat.translations?.[l] ?? nat.translations?.en ?? nat.name ?? '';
};

const emit = defineEmits(['submit', 'cancel', 'validation-error', 'update:form-data']);
const toast = useToast();

const formData = ref({ ...props.initialData });
const errors = ref({});
const fileInput = ref(null);
const loadingImage = ref(false);
const previewImage = ref(props.initialData.image_url || null);
const nationalities = ref([]);
const isNationalitiesLoading = ref(false);
const isSubmitting = ref(false);
const jobTitles = ref([]);

const fetchJobTitles = async () => {
  try {
    const response = await api.get('/job-titles', { params: { category: 'teacher', all: 1 } });
    jobTitles.value = response.data.data || response.data || [];
  } catch (e) {
    console.error('Error fetching job titles:', e);
    jobTitles.value = [];
  }
};

const isLibyan = computed(() => {
  if (!formData.value.nationality_id || nationalities.value.length === 0) 
    return false;

  const selectedNationality = nationalities.value.find(
    n => n.id.toString() === formData.value.nationality_id.toString()
  );
  return selectedNationality?.code === "LY";
});

const maxDate = computed(() => new Date().toISOString().split('T')[0]);

const validateField = (field) => {
  if (!formData.value[field]) {
    errors.value[field] = t('validation.required');
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateEmail = () => {
  if (!formData.value.email) {
    errors.value.email = t('validation.required');
    return false;
  }
  
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    errors.value.email = t('validation.invalidEmail');
    return false;
  }
  
  delete errors.value.email;
  return true;
};

const validatePassword = () => {
  if (!props.isEditMode && !formData.value.password) {
    errors.value.password = t('validation.required');
    return false;
  }
  
  if (formData.value.password && formData.value.password.length < 8) {
    errors.value.password = t('validation.passwordMinLength');
    return false;
  }
  
  delete errors.value.password;
  return true;
};

const validateConfirmPassword = () => {
  if (formData.value.password !== formData.value.password_confirmation) {
    errors.value.password_confirmation = t('validation.passwordMismatch');
    return false;
  }
  
  delete errors.value.password_confirmation;
  return true;
};

const validatePhone = () => {
  if (!formData.value.phone_num) {
    errors.value.phone_num = t('validation.required');
    return false;
  }
  
  if (!/^[\d\s+-]{7,15}$/.test(formData.value.phone_num)) {
    errors.value.phone_num = t('validation.invalidPhone');
    return false;
  }
  
  delete errors.value.phone_num;
  return true;
};

const validateAddress = () => {
  if (!formData.value.address) {
    errors.value.address = t('validation.required');
    return false;
  }
  
  delete errors.value.address;
  return true;
};

const validateNationalNumber = () => {
  if (isLibyan.value && !formData.value.national_num) {
    errors.value.national_num = t('validation.nationalNumRequired');
    return false;
  }

  if (isLibyan.value && !/^\d{12}$/.test(formData.value.national_num)) {
    errors.value.national_num = t('validation.nationalNum11Digits');
    return false;
  }

  delete errors.value.national_num;
  return true;
};

const validatePassport = () => {
  if (!isLibyan.value && !formData.value.passport_num) {
    errors.value.passport_num = t('validation.passportRequired');
    return false;
  }

  if (!isLibyan.value && !/^[a-zA-Z0-9]{6,20}$/.test(formData.value.passport_num)) {
    errors.value.passport_num = t('validation.passportFormat');
    return false;
  }

  delete errors.value.passport_num;
  return true;
};

const validateHiringDate = () => {
  if (!formData.value.hiring_date) {
    errors.value.hiring_date = t('validation.required');
    return false;
  }
  
  const hiringDate = new Date(formData.value.hiring_date);
  const today = new Date();
  
  if (hiringDate > today) {
    errors.value.hiring_date = t('validation.hiringDateFuture');
    return false;
  }
  
  delete errors.value.hiring_date;
  return true;
};

// If the admin types a salary and then switches to "per lesson", the field
// disappears but a v-model binding keeps holding the old value in memory —
// it would otherwise still get submitted invisibly. Clear it the moment the
// type changes so what's hidden is also what's actually saved.
watch(
  () => formData.value.salary_type,
  (type) => {
    if (type === 'per_lesson') {
      formData.value.salary = '';
      delete errors.value.salary;
    }
  }
);

const validateSalary = () => {
  // A per-lesson teacher has no fixed salary field to fill in — nothing to
  // validate, and the field itself is hidden (see the template's v-if).
  if (formData.value.salary_type === 'per_lesson') {
    delete errors.value.salary;
    return true;
  }

  if (!formData.value.salary) {
    errors.value.salary = t('validation.required');
    return false;
  }
  
  if (formData.value.salary <= 0) {
    errors.value.salary = t('validation.salaryPositive');
    return false;
  }
  
  delete errors.value.salary;
  return true;
};

const validateRegisterDate = () => {
  if (!formData.value.register_date) {
    errors.value.register_date = t('validation.required');
    return false;
  }

  delete errors.value.register_date;
  return true;
};

const validateAccountNumber = () => {
  const val = formData.value.account_number;
  if (val && !/^\d{1,15}$/.test(val)) {
    errors.value.account_number = t('teachers.accountNumberError');
    return false;
  }
  delete errors.value.account_number;
  return true;
};

const validateIban = () => {
  const val = formData.value.iban;
  if (val && val.length > 23) {
    errors.value.iban = t('teachers.ibanError');
    return false;
  }
  delete errors.value.iban;
  return true;
};

const fetchNationalities = async () => {
  isNationalitiesLoading.value = true;
  try {
    const response = await api.get('/nationalities', {
      params: { locale: (locale.value || 'ar').split('-')[0] }
    });
    nationalities.value = response.data.data;
  } catch (e) {
    console.error('Error fetching nationalities:', e);
    nationalities.value = [];
  } finally {
    isNationalitiesLoading.value = false;
  }
};
const handleNationalityChange = () => {
  if (!isLibyan.value) {
    formData.value.national_num = "";
  } else {
    formData.value.passport_num = "";
  }
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) {
    clearImage();
    return;
  }

  const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
  if (!validTypes.includes(file.type.toLowerCase()) || file.size > 2 * 1024 * 1024) {
    toast.error(t('validation.imageTypeSize'));
    clearImage();
    return;
  }

  formData.value.image = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    previewImage.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const clearImage = () => {
  formData.value.image = null;
  previewImage.value = null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const validateForm = () => {
  errors.value = {};
  const validations = [
    validateField('username'),
    validateEmail(),
    validateField('gender'),
    validateField('nationality_id'),
    validateNationalNumber(),
    validatePassport(),
    validatePhone(),
    validateAddress(),
    validateField('qualification'),
    validateHiringDate(),
    validateSalary(),
    validateRegisterDate(),
  ];

  if (!props.isEditMode) {
    validations.push(validatePassword());
    validations.push(validateConfirmPassword());
  }

  return validations.every(valid => valid);
};

const setBackendErrors = (backendErrors) => {
  errors.value = {};
  
  for (const field in backendErrors) {
    if (field in formData.value) {
      errors.value[field] = backendErrors[field][0];
    }
  }

  const firstErrorField = Object.keys(backendErrors)[0];
  nextTick(() => {
    const input = document.getElementById(firstErrorField);
    if (input) input.focus();
  });
};

const handleSubmit = async () => {
  isSubmitting.value = true; 
  
  try {
    if (!validateForm()) {
      emit("validation-error");
      toast.warning(t('validation.correctErrors'));
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData.value).forEach((key) => {
      if (key === 'is_active') {
        // FormData turns true/false into "true"/"false" strings which Laravel rejects.
        // Send 1/0 so Laravel's boolean validation accepts it.
        formDataToSend.append('is_active', formData.value.is_active ? 1 : 0);
      } else if (formData.value[key] !== null && formData.value[key] !== undefined) {
        formDataToSend.append(key, formData.value[key]);
      }
    });

    if (props.isEditMode) {
      formDataToSend.append("_method", "PUT");
    }

    await emit("submit", formDataToSend);
    
  } catch (error) {
    if (error.response?.status === 422) {
      setBackendErrors(error.response.data.errors);
      const firstError = Object.values(error.response.data.errors)[0][0];
      toast.error(firstError);
    } else {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.message || t('validation.submissionError'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const submitForm = () => {
  handleSubmit();
};

const clearError = (fieldName) => {
  if (errors.value[fieldName]) {
    delete errors.value[fieldName];
  }
};

defineExpose({
  validateForm,
  submitForm,
  setBackendErrors
});

onMounted(() => {
  fetchNationalities();
  fetchJobTitles();
});

watch(() => props.initialData, (newData) => {
  // Cast is_active to proper boolean — API may return "1", "0", 1, 0, or true/false
  const rawActive = newData.is_active;
  const isActive = rawActive == null
    ? true
    : (rawActive !== false && rawActive !== 0 && rawActive !== "0");

  formData.value = {
    ...newData,
    password: '',
    password_confirmation: '',
    image: formData.value.image,
    image_url: newData.image_url || formData.value.image_url,
    is_active: isActive,
    // A per-lesson teacher has no fixed salary — the field is hidden, so
    // don't carry over a stale salary value that was saved before the
    // teacher was switched to this type (or before this field existed).
    salary: newData.salary_type === 'per_lesson' ? '' : newData.salary,
  };
  previewImage.value = newData.image_url || null;

  // Sync bank preset
  const bv = newData.bank_name || '';
  bankNamePreset.value = bv === '' ? '' : (libyaBanks.includes(bv) ? bv : '_other');

  // Sync specialization preset
  const sv = newData.specialization || '';
  specializationPreset.value = sv === '' ? '' : (specializations.includes(sv) ? sv : '_other');
}, { immediate: true, deep: true });
watch(formData, (newVal) => {
  emit('update:form-data', newVal);
}, { deep: true });

</script>
